import { useState } from 'react';
import { ActionIcon, Badge, Box, Button, Divider, Drawer, Group, ScrollArea, SimpleGrid, Stack, Table, Text, TextInput, Tooltip } from '@mantine/core';
import { IconArrowBackUp, IconChevronDown, IconPlayerPauseFilled, IconPlayerPlayFilled, IconPlayerSkipBackFilled, IconPlayerSkipForwardFilled, IconRotateClockwise } from '@tabler/icons-react';
import { fmt, poiWindow, segmentSide, segmentTitle, type ClockKey, type Round, type SideIndex } from '@flow/core';
import { TimerDisplay } from '../../ui/TimerDisplay';
import { K } from '../../ui/Kbd';
import type { TimerApi } from './useTimer';
import { TimerSoundMenu } from './TimerSound';
import { Brand } from '../../ui/Brand';

const sideColor = (s: SideIndex | null) => (s === null ? 'var(--side-none)' : `var(--side-${s})`);

const parseMmSs = (v: string): number | null => {
  const m = v.trim().match(/^(\d{1,2})(?::([0-5]?\d))?$/);
  if (!m) return null;
  return (Number(m[1]) * 60 + Number(m[2] ?? 0)) * 1000;
};

const Adjust = ({ t, clock, steps }: { t: TimerApi; clock: ClockKey; steps: number[] }) => (
  <Group gap={4} wrap="nowrap">
    {steps.map((s) => (
      <Button key={s} size="compact-xs" variant="default" onClick={() => void t.send({ t: 'adjust', clock, deltaMs: s * 1000 })} aria-label={`${s > 0 ? 'add' : 'remove'} ${Math.abs(s)} seconds`}>
        {s > 0 ? `+${s}` : `−${Math.abs(s)}`}
      </Button>
    ))}
  </Group>
);

const PrepChip = ({ round, t, side, compact }: { round: Round; t: TimerApi; side: SideIndex; compact?: boolean }) => {
  const key: ClockKey = side === 0 ? 'prep0' : 'prep1';
  const v = t.view(key);
  if (!v || round.format.prepSec === 0) return null; // e.g. Parli: all prep happens before the round
  const on = v.running;
  return (
    <Tooltip label={`${round.format.sideLabels[side]} prep: tap to ${on ? 'stop' : 'start'} (⌥${side + 1})`}>
      <Button
        size={compact ? 'compact-xs' : 'compact-sm'}
        variant={on ? 'filled' : 'default'}
        color={on ? 'dark' : undefined}
        onClick={() => void t.send({ t: 'togglePrep', side })}
        data-testid={`prep-${side}`}
        styles={{ label: { gap: 6 } }}
      >
        <span style={{ color: on ? undefined : sideColor(side), fontWeight: 600 }}>{round.format.sideLabels[side]} prep</span>
        <span className="tabular">{v.label}</span>
      </Button>
    </Tooltip>
  );
};

/** Parli: whether points of information are allowed right now (not in a constructive's first or last minute). */
const PoiBadge = ({ round, t }: { round: Round; t: TimerApi }) => {
  const w = poiWindow(round.format, t.segment, t.view('speech'));
  if (!w) return null;
  return (
    <Tooltip label={w === 'open' ? 'Points of information are allowed now.' : "Protected time: no points of information in a constructive's first or last minute."}>
      <Badge variant={w === 'open' ? 'filled' : 'outline'} color={w === 'open' ? 'green' : 'gray'} size="sm" data-testid="poi-window">{w === 'open' ? 'POIs open' : 'Protected'}</Badge>
    </Tooltip>
  );
};

/** Compact always-visible timer bar; opens the full control drawer. */
export const TimerStrip = ({ round, t, compact }: { round: Round; t: TimerApi; compact?: boolean }) => {
  const [open, setOpen] = useState(false);
  const v = t.view('speech');
  const seg = t.segment;
  const side = seg ? segmentSide(round, seg) : null;
  return (
    <>
      <Group
        gap="xs"
        px={compact ? 6 : 'sm'}
        py={compact ? 2 : 6}
        wrap="nowrap"
        style={{
          borderBottom: compact ? undefined : '1px solid var(--grid-line)',
          background: t.flash !== null ? 'var(--mantine-color-yellow-light)' : undefined,
          transition: 'background 300ms',
        }}
        data-testid="timer-strip"
      >
        <Box style={{ minWidth: 0 }}>
          <Text size="xs" c="dimmed" lh={1.1} lineClamp={1}>
            {seg ? <><b style={{ color: sideColor(side) }}>{seg.id}</b> · {segmentTitle(round, seg)}</> : 'Round complete'}
          </Text>
        </Box>
        {v && <TimerDisplay label={v.label} phase={v.phase} size={compact ? 20 : 26} testId="speech-time" />}
        <PoiBadge round={round} t={t} />
        <Tooltip label="Previous speech (⌥,)">
          <ActionIcon
            size={compact ? 'md' : 'lg'}
            variant="default"
            onClick={() => t.state && void t.send({ t: 'goto', index: Math.max(0, t.state.cursor - 1) })}
            disabled={!t.state || t.state.cursor === 0}
            aria-label="Previous speech"
            data-testid="timer-prev"
          >
            <IconPlayerSkipBackFilled size={16} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Start / pause (⌥Space)">
          <ActionIcon size={compact ? 'md' : 'lg'} variant="filled" color="dark" onClick={() => void t.send({ t: 'toggle' })} disabled={t.done} aria-label={v?.running ? 'Pause' : 'Start'} data-testid="timer-toggle">
            {v?.running ? <IconPlayerPauseFilled size={16} /> : <IconPlayerPlayFilled size={16} />}
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Done: next speech (⌥.)">
          <ActionIcon size={compact ? 'md' : 'lg'} variant="default" onClick={() => void t.send({ t: 'done' })} disabled={t.done} aria-label="Done, next speech" data-testid="timer-next">
            <IconPlayerSkipForwardFilled size={16} />
          </ActionIcon>
        </Tooltip>
        {!compact && <Adjust t={t} clock="speech" steps={[-15, 15]} />}
        <Box style={{ flex: 1 }} />
        <PrepChip round={round} t={t} side={0} compact={compact} />
        <PrepChip round={round} t={t} side={1} compact={compact} />
        <TimerSoundMenu size={compact ? 'md' : 'lg'} />
        <Tooltip label="All timer controls">
          <ActionIcon onClick={() => setOpen(true)} aria-label="All timer controls" data-testid="timer-more">
            <IconChevronDown size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>
      <TimerDrawer round={round} t={t} opened={open} onClose={() => setOpen(false)} />
    </>
  );
};

const TimerDrawer = ({ round, t, opened, onClose }: { round: Round; t: TimerApi; opened: boolean; onClose: () => void }) => {
  const [setValue, setSetValue] = useState('');
  const [setError, setSetError] = useState<string | null>(null);
  const v = t.view('speech');
  const st = t.state;
  const doSet = (clock: ClockKey) => {
    const ms = parseMmSs(setValue);
    if (ms === null) {
      setSetError('Use m:ss, like 2:15.');
      return;
    }
    setSetError(null);
    void t.send({ t: 'set', clock, remainingMs: ms });
    setSetValue('');
  };
  return (
    <Drawer opened={opened} onClose={onClose} position="bottom" size="auto" title="Timer" zIndex={450}>
      <Stack gap="md" pb="md">
        <Group justify="space-between" wrap="wrap">
          <Group gap="sm">
            {v && <TimerDisplay label={v.label} phase={v.phase} size={40} />}
            <Text size="sm" c="dimmed">{t.segment ? segmentTitle(round, t.segment) : 'Round complete'}{v && v.phase === 'grace' ? ' · grace' : v?.phase === 'over' ? ' · over time' : ''}</Text>
          </Group>
          <Group gap={6}>
            <Button leftSection={v?.running ? <IconPlayerPauseFilled size={14} /> : <IconPlayerPlayFilled size={14} />} variant="filled" onClick={() => void t.send({ t: 'toggle' })} disabled={t.done}>{v?.running ? 'Pause' : 'Start'}</Button>
            <Button leftSection={<IconPlayerSkipForwardFilled size={14} />} onClick={() => void t.send({ t: 'done' })} disabled={t.done}>Done → next</Button>
            <Button leftSection={<IconRotateClockwise size={14} />} onClick={() => void t.send({ t: 'restart' })} disabled={t.done}>Restart</Button>
            <Button leftSection={<IconArrowBackUp size={14} />} onClick={() => void t.undo()} disabled={!t.rec?.undo.length} data-testid="timer-undo">Undo</Button>
          </Group>
        </Group>
        <Group gap="lg" wrap="wrap">
          <Box>
            <Text size="xs" c="dimmed" mb={4}>Correct speech time</Text>
            <Adjust t={t} clock="speech" steps={[-30, -15, -5, 5, 15, 30]} />
          </Box>
          <Box>
            <Text size="xs" c="dimmed" mb={4}>Set exact time left</Text>
            <Group gap={4} align="flex-start">
              <TextInput size="xs" w={80} placeholder="2:15" value={setValue} onChange={(e) => setSetValue(e.currentTarget.value)} error={setError} aria-label="Set time" onKeyDown={(e) => e.key === 'Enter' && doSet('speech')} />
              <Button size="xs" onClick={() => doSet('speech')}>Set speech</Button>
              {round.format.prepSec > 0 && <Button size="xs" onClick={() => doSet('prep0')}>Set {round.format.sideLabels[0]} prep</Button>}
              {round.format.prepSec > 0 && <Button size="xs" onClick={() => doSet('prep1')}>Set {round.format.sideLabels[1]} prep</Button>}
            </Group>
          </Box>
        </Group>
        {round.format.prepSec > 0 && <SimpleGrid cols={2}>
          {([0, 1] as SideIndex[]).map((s) => (
            <Group key={s} gap="xs">
              <PrepChip round={round} t={t} side={s} />
              <Adjust t={t} clock={s === 0 ? 'prep0' : 'prep1'} steps={[-15, 15]} />
            </Group>
          ))}
        </SimpleGrid>}
        <Divider />
        <Box>
          <Text size="xs" c="dimmed" mb={4}>Speeches: tap to jump (the log keeps actual times)</Text>
          <Group gap={4}>
            {round.format.segments.map((seg, i) => {
              const actual = st?.actuals[seg.id];
              const current = st?.cursor === i;
              return (
                <Button key={seg.id} size="compact-xs" variant={current ? 'filled' : 'default'} color={current ? 'dark' : undefined} onClick={() => void t.send({ t: 'goto', index: i })}>
                  <span style={{ color: current ? undefined : sideColor(segmentSide(round, seg)) }}>{seg.id}</span>
                  {actual !== undefined && <Badge ml={4} size="xs" variant="light" color={actual > seg.durationSec * 1000 + round.format.graceSec * 1000 ? 'red' : 'gray'}>{fmt(actual).replace('+', '')}</Badge>}
                </Button>
              );
            })}
          </Group>
        </Box>
        <Box>
          <Text size="xs" c="dimmed" mb={4}>Log</Text>
          <ScrollArea h={120}>
            <Table fz="xs" verticalSpacing={2}>
              <Table.Tbody>
                {[...(st?.log ?? [])].reverse().map((l, i) => (
                  <Table.Tr key={i}>
                    <Table.Td w={110} className="tabular" c="dimmed" style={{ whiteSpace: 'nowrap' }}>{new Date(l.at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' })}</Table.Td>
                    <Table.Td>{l.what}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Box>
        <Text size="xs" c="dimmed">
          <K>⌥Space</K> start/pause · <K>⌥.</K> next · <K>⌥,</K> previous · <K>⌥1</K>/<K>⌥2</K> prep · <K>⌥[</K>/<K>⌥]</K> −/+15s. Time is saved as timestamps, so a reload or tab switch never loses time.
          On iPad, alerts can't sound while <Brand /> is in the background.
        </Text>
      </Stack>
    </Drawer>
  );
};
